import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { NgOptimizedImage, NgTemplateOutlet } from "@angular/common";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { IonicModule, MenuController } from "@ionic/angular";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBuilding,
  faCalculator,
  faChartColumn,
  faChevronDown,
  faCircleQuestion,
  faFileChartColumn,
  faGear,
  faHandshake,
  faInboxFull,
  faLifeRing,
  faShop,
  faUser,
  faUsers,
  faXmark,
} from "@fortawesome/pro-light-svg-icons";

import { AuthService } from "../auth/auth.service";
import { profile } from "../API.service";
import { environment } from "../../environments/environment";
import { LaunchDarklyService } from "../services/launch-darkly.service";
import { UserGroupService } from "../services/user-group.service";
import { OnlineStatusService } from "../services/online-status.service";
import { ProfilePictureService } from "../services/profile-picture.service";

interface NavItem {
  readonly label: string;
  readonly link: string;
  /** Top-level links have an icon; links inside a group are indented instead. */
  readonly icon?: IconDefinition;
  readonly disabled?: boolean;
}

interface NavGroup {
  readonly label: string;
  readonly icon: IconDefinition;
  readonly disabled?: boolean;
  readonly items: readonly NavItem[];
}

/**
 * Signed-in layout: the side menu (the L&P standard from LP-FIN-APP, Font Awesome Pro Light, see
 * knowledge/icons/font-awesome.md) inside Ionic's split pane, so pages keep their <ion-menu-button>
 * and the drawer below `lg`.
 */
@Component({
  selector: "app-layout",
  templateUrl: "./layout.page.html",
  styleUrls: ["./layout.page.scss"],
  // Not OnPush (unlike AGENTS.md's default): every page renders inside this component's
  // <ion-router-outlet>, so an OnPush shell stops change detection from reaching pages that set
  // plain properties asynchronously (e.g. /admin/sales/list stays on its loader until a click).
  // Switch to OnPush only once every routed page is OnPush or signal-based.
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [IonicModule, RouterLink, RouterLinkActive, NgTemplateOutlet, NgOptimizedImage, FaIconComponent],
})
export class LayoutPage implements OnInit {
  private readonly router = inject(Router);
  private readonly menu = inject(MenuController);
  private readonly authservice = inject(AuthService);
  private readonly onlineStatusService = inject(OnlineStatusService);
  private readonly profilePictureService = inject(ProfilePictureService);

  constructor() {
    // Not used here, but both start work when created (LaunchDarkly client, Cognito group
    // listener) and the layout has always been what creates them after sign-in.
    inject(LaunchDarklyService);
    inject(UserGroupService);
  }

  protected readonly appVersion = environment.appVersion;

  protected readonly faChevronDown = faChevronDown;
  protected readonly faLifeRing = faLifeRing;
  protected readonly faXmark = faXmark;

  protected readonly modalOpen = signal(false);

  protected readonly profile = signal<profile | null>(null);
  protected readonly profilePictureUrl = signal<string | null>(null);
  protected readonly initials = computed(() => {
    const p = this.profile();
    return `${p?.firstName?.charAt(0) ?? ""}${p?.lastName?.charAt(0) ?? ""}`.toUpperCase();
  });

  private readonly isAdmin = signal(false);
  private readonly isManager = signal(false);
  private readonly isCoordinator = signal(false);
  private readonly isPromotionManager = signal(false);
  private readonly isValuationAdmin = signal(false);

  protected readonly showManagement = computed(
    () => this.isAdmin() || this.isManager() || this.isCoordinator(),
  );
  protected readonly showAdministration = computed(() => this.isAdmin() || this.isValuationAdmin());

  protected readonly crmItems: readonly NavItem[] = [
    { link: "/user/sales/list", label: "Deals", icon: faHandshake },
    { link: "/user/valuations/list-valuations", label: "Rapports d'évaluation", icon: faFileChartColumn },
    { link: "/user/my-commissions", label: "Commissions", icon: faCalculator },
    { link: "/user/user-profile", label: "Profile", icon: faUser },
    { link: "/user/faq", label: "FAQ", icon: faCircleQuestion },
  ];

  // Coordinators (without the manager or admin group) get a read-only subset:
  // the deals and valuation reports of their agencies, no per-agent pages.
  protected readonly managementGroups = computed<readonly NavGroup[]>(() => {
    const isFullManager = this.isManager() || this.isAdmin();
    const groups: NavGroup[] = [
      {
        label: "Mes agences",
        icon: faShop,
        items: [
          ...(isFullManager ? [{ link: "/manager/list-user", label: "Utilisateurs" }] : []),
          { link: "/manager/deals", label: "Deals" },
          { link: "/manager/valuations", label: "Rapports d'évaluation" },
        ],
      },
    ];
    if (isFullManager) {
      const disabled = !this.isPromotionManager();
      groups.push({
        label: "Immobilier neuf",
        icon: faBuilding,
        disabled,
        items: [
          { link: "/manager/promotions/list", label: "Projets", disabled },
          { link: "/manager/promotions/deals", label: "Deals", disabled },
        ],
      });
    }
    return groups;
  });

  protected readonly adminItems = computed<readonly NavItem[]>(() => {
    if (this.isAdmin()) {
      return [
        { link: "/admin/sales/list", label: "Sales", icon: faInboxFull },
        { link: "/admin/users", label: "Utilisateurs", icon: faUsers },
        { link: "/admin/reports", label: "Rapports", icon: faChartColumn },
        { link: "/admin/settings", label: "Paramètres", icon: faGear },
      ];
    }
    if (this.isValuationAdmin()) {
      return [{ link: "/admin/settings", label: "Paramètres", icon: faGear }];
    }
    return [];
  });

  /** Labels of the expanded groups. */
  protected readonly openGroups = signal<ReadonlySet<string>>(new Set());

  async ngOnInit() {
    this.isManager.set(await this.authservice.isManager());
    this.isCoordinator.set(await this.authservice.isCoordinator());
    this.isAdmin.set(await this.authservice.isAdmin());
    this.isPromotionManager.set(await this.authservice.isPromotionManager());
    this.isValuationAdmin.set(await this.authservice.isValuationAdmin());

    const resp = await this.authservice.getUserProfile();
    const p = (resp?.data?.getProfile ?? null) as profile | null;
    this.profile.set(p);

    try {
      this.profilePictureUrl.set(p ? await this.profilePictureService.getProfilePicture(p.id) : null);
    } catch {
      // Best effort: the initials show instead.
      this.profilePictureUrl.set(null);
    }

    // Initialize online status tracking after successful authentication
    try {
      await this.onlineStatusService.initialize();
    } catch (error) {
      console.error("Failed to initialize online status tracking:", error);
    }
  }

  protected toggleGroup(label: string): void {
    this.openGroups.update((open) => {
      const next = new Set(open);
      if (!next.delete(label)) {
        next.add(label);
      }
      return next;
    });
  }

  /** Closes the drawer below `lg`; a no-op while the split pane shows the menu. */
  protected closeMenu(): void {
    void this.menu.close();
  }

  protected logout(): void {
    this.onlineStatusService.stop();
    sessionStorage.removeItem("hasInitialRedirect");
    localStorage.clear();
    this.closeMenu();
    this.router.navigate(["/login"]);
  }

  protected openSupport(): void {
    this.closeMenu();
    this.modalOpen.set(true);
  }

  protected cancel(): void {
    this.modalOpen.set(false);
  }

  // Close on Escape key
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.cancel();
    }
  }
}
